"use client";

import Image from "next/image";
import Link from "next/link";

import cn from "classnames";
import { Image as ImageType } from "sanity";

import { ChevronRightIcon } from "@/components/icons/chevronRight.icon";
import { Text } from "@/components/text/text.component";

import { PRODUCT_DRAWER_QUERY_PARAM } from "@/constants/product.constants";
import { urlFor } from "@/sanity/utils";

/** Product Included Products */
export const ProductOptionsProductValue = ({
  id,
  optionId,
  title,
  description,
  image,
  product,
  selected,
  onChange,
}: ProductOptionsProductValueProps) => {
  const url = image?.[0]
    ? urlFor(image[0])?.width(320)?.height(320)?.url()
    : null;

  return (
    <li data-testid="included-product-option" className="list-none">
      <label
        htmlFor={id}
        className={cn(
          "flex h-full w-full cursor-pointer flex-row items-center gap-4 overflow-hidden rounded-2xl border-2 p-4 md:px-6 md:py-2 lg:py-6",
          { "border-secondary bg-primary-100": selected },
        )}>
        <div className="flex min-w-1/2 flex-1 flex-col gap-3 md:gap-2">
          <div className="flex flex-col gap-1">
            <Text variant="body2" data-testid="included-product-option-name">
              {title}
            </Text>
            <Text
              data-testid="included-product-option-description"
              className="line-clamp-3 md:mb-6 md:line-clamp-none">
              {description}
            </Text>
          </div>
          {product?.id ? (
            <Link
              href={`?${PRODUCT_DRAWER_QUERY_PARAM}=${product.id}`}
              data-testid="included-product-option-view-details"
              className="flex w-fit gap-1 underline underline-offset-3">
              <Text
                variant="body4"
                component="span"
                className="text-greyscale-body underline">
                View details
              </Text>

              <ChevronRightIcon
                width={16}
                height={16}
                className="shrink-0 self-center"
              />
            </Link>
          ) : null}
        </div>
        {url ? (
          <div className="flex w-2/5 max-w-36 shrink-0 items-center justify-end md:h-36 md:max-w-36">
            <Image
              data-testid="included-product-option-image"
              src={url}
              alt={product?.name || ""}
              width={200}
              height={200}
              className="max-h-44 w-auto max-w-full object-contain md:max-h-full"
            />
          </div>
        ) : null}
        <input
          required
          data-testid="included-product-option-input"
          id={id}
          type="radio"
          // ENHANCEMENT: add sanity toggle to support option and addon
          name="option"
          className="hidden"
          value={`${optionId}:${id}`}
          checked={selected}
          onChange={() => id && onChange?.(id)}
        />
      </label>
    </li>
  );
};

export type ProductOptionsProductValueProps = {
  id?: string;
  optionId: string;
  title?: string | null;
  description?: string | null;
  image?: ImageType[] | null;
  product?: {
    id: string;
    slug: string;
    name: string;
    sku: string;
  } | null;
  selected?: boolean;
  fieldsetName: string;
  onChange?: (id: string) => void;
};
